import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useGetMapperMutation } from '../../Redux/Admin_Tasks/DataMapperSlice'
import DataMapperFilter from './DataMapperFilters'
import MapperActions from './MapperActions'
import { Accordion } from '../../GenericComponents/accordion'
import { FoundationsGenericToaster } from '../../GenericComponents/FoundationsGenericToaster'

interface ResultType {
    [key: string]: any // Allows dynamic keys with any type of value
}
interface MapperActionType {
    Action: string
    Rows: ResultType[]
    ParentFields: ResultType[]
}

declare let window: any
const UnicornApp = window.unicornInstance as any

function IndividualMapper({
    Key,
    value,
    description,
    help,
    defaultMapper,
    Columns,
    GotoMapperList,
}: {
    Key: string
    value: string
    description: string
    help: string | null
    defaultMapper: null
    Columns: string[]
    GotoMapperList: () => void
}) {
    const [Mapper, setMapper] = useState(defaultMapper)
    const [statusCode, setStatusCode] = useState(-1)
    const [MapperResults, setMapperResults] = useState<ResultType[]>([])
    const [ParentFields, setParentFields] = useState<ResultType[]>([])
    const [ColumnNames, setColumnNames] = useState<string[]>(Columns)
    const [showActionEdit, setShowActionEdit] = useState<boolean>(true)
    const [showActionDelete, setShowActionDelete] = useState<boolean>(true)
    const [ActionMode, setActionMode] = useState(false)
    const [FilterValues, SetFilterValues] = useState<ResultType>({})
    const [MapperActionData, setMapperActionData] = useState<MapperActionType>({
        Action: '',
        Rows: [],
        ParentFields: [],
    })
    const [selectedCheckboxes, setSelectedCheckboxes] = useState<{ [key: number]: number[] }>({});
    const [checkedBoxes, setCheckedBoxes] = useState<number[]>([])
    const [currentPage, setCurrentPage] = useState(1)
    const [recordsPerPage, setRecordsPerPage] = useState(50) // Default to 50

    const paginatedData = MapperResults.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
    )
    const totalPages = Math.ceil(MapperResults.length / recordsPerPage)

    const [getMapper, { isLoading }] = useGetMapperMutation()

    const SetFilters = (payload: any) => {
        setMapper(payload.data)
        const attributeList = payload.data.Value?.AttributeList
        const columns =
            attributeList && attributeList[0]?.Prop0
                ? attributeList[0].Prop0.split(',').slice(1)
                : []
        setColumnNames(columns)
    }

    const [accordionHeader, setAccordionHeader] = useState('Show Help');
    //FP-10590 : Setting state of accordionHeader toggle text based on 'active' class
    const handleAccordionClick = (isOpen: boolean) => {
        setAccordionHeader(isOpen ? 'Hide Help' : 'Show Help');
    };

    const FilterUpdated = async (DropdownValues: ResultType) => {
        // Clean any numeric keys again here, just in case
        const cleanedValues: { [key: string]: any } = {}
        Object.entries(DropdownValues).forEach(([key, val]) => {
            if (isNaN(Number(key))) {
                cleanedValues[key] = val
            }
        })
        if (Object.values(DropdownValues).every((value) => value === '')) {
            SetFilterValues(DropdownValues)
            setStatusCode(-1)
        } else {
            SetFilterValues(DropdownValues)
            const GetFiltersResponse = await getMapper({
                key: Key,
                action: 'ButtonAction',
                // data: [DropdownValues],
                data: [
                    {
                        ...cleanedValues,
                        Available: "",
                        DisplayOrder: "",
                        key: Key,
                    }
                ]
            })
            if (!isLoading) {
                if (GetFiltersResponse.data.statusMessage.toLowerCase() === 'success') {
                    SetFilters(GetFiltersResponse.data)
                } else {
                    FoundationsGenericToaster('error', 'Could Not Load Filters')
                }
            }
            GetMapperResults(DropdownValues)
        }
    }

    const GetMapperResults = async (DropdownValues?: ResultType) => {
        setStatusCode(0)
        setCheckedBoxes([])
        const TableDataResponse = await getMapper({
            key: Key,
            action: 'Read',
            data: DropdownValues ? [DropdownValues] : [FilterValues],
        })
        if (!isLoading) {
            if (TableDataResponse.data.statusMessage.toLowerCase() === 'success') {
                setMapperResults(TableDataResponse.data.data.Value?.Data || [])//FP-10466 If Data is not found, an empty array will be used instead.
                setParentFields(TableDataResponse.data.data.Value?.ParentFields)
                setStatusCode(TableDataResponse.data.statusCode)
                if(TableDataResponse.data.data.Value.Actions)
                {
                    if (!TableDataResponse.data.data.Value.Actions?.[0].Prop0.split(',').includes('Edit')) 
                    {
                        setShowActionEdit(false);
                    }
                    if (!TableDataResponse.data.data.Value.Actions?.[0].Prop0.split(',').includes('Delete')) 
                    {
                        setShowActionDelete(false);
                    }
                }
            } else {
                setStatusCode(500)
                FoundationsGenericToaster('error', 'An error occurred fetching the data')
            }
        }
    }

    const LoadMapperResults = (Status: string, Message: string) => {
        if (Status === 'success') {
            FoundationsGenericToaster("success", Message)
            GetMapperResults()
        }
        else {
            if (Message){
                FoundationsGenericToaster("error", Message)
            }
        }
        setActionMode(false)
    }

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setRecordsPerPage(Number(e.target.value))
        setCurrentPage(1) // Reset to first page when changing page size
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    const handleCheckboxChange = (index: number) => {
        setSelectedCheckboxes((prev) => {
            const updatedPageCheckboxes = prev[currentPage] || [];
            if (updatedPageCheckboxes.includes(index)) {
                return {
                    ...prev,
                    [currentPage]: updatedPageCheckboxes.filter((i) => i !== index), // Deselect checkbox
                };
            } else {
                return {
                    ...prev,
                    [currentPage]: [...updatedPageCheckboxes, index], // Select checkbox
                };
            }
        })
    }
       

    const AddRow = () => {
        setActionMode(true)
        setMapperActionData({
            Action: 'Add',
            Rows: [FilterValues],
            ParentFields: [],
        })
    }
    const EditRow = (index: number) => {
        setActionMode(true)
        // var selected = checkedBoxes.length > 0 ? checkedBoxes : [index]
        const currentPageSelections = selectedCheckboxes[currentPage] || [];
        const selected = currentPageSelections.length > 0 ? currentPageSelections : [index]
        const EditResults = MapperResults.filter((_, index) => selected.includes(index))
        setMapperActionData({
            Action: 'Edit',
            Rows: EditResults,
            ParentFields: ParentFields,
        })
    }

    const DeleteRow = (index: number) => {
        setActionMode(true)
        // var selected = checkedBoxes.length > 0 ? checkedBoxes : [index]
        const currentPageSelections = selectedCheckboxes[currentPage] || [];
        const selected = currentPageSelections.length > 0 ? currentPageSelections : [index]
        const DeleteResults = MapperResults.filter((_, index) => selected.includes(index))
        setMapperActionData({
            Action: 'Delete',
            Rows: DeleteResults,
            ParentFields: [],
        })
    }

    const generatePaginationButtons = () => {
        const buttons: JSX.Element[] = []
        // Add "First" and "Previous" buttons
        buttons.push(
            <button
                id="first"
                className={
                    currentPage === 1 ? 'dt-paging-button disabled first' : 'dt-paging-button first'
                }
                onClick={() => handlePageChange(1)}
            >
                &laquo;
            </button>
        )
        buttons.push(
            <button
                id="prev"
                className={
                    currentPage === 1
                        ? 'dt-paging-button disabled previous'
                        : 'dt-paging-button previous'
                }
                onClick={(e) => {
                    if (currentPage === 1) {
                        e.preventDefault(); 
                        return; // FP-10424 Prevent the page change if we're on the First page 
                    } 
                    handlePageChange(currentPage - 1);
                }}
            >
                &lt;
            </button>
        )
        if (totalPages > 7) {
            if (currentPage < 5) {
                for (let i = 1; i <= 5; i++) {
                    buttons.push(
                        <button
                            id={i.toString()}
                            disabled={currentPage === i}
                            className={
                                currentPage === i ? 'dt-paging-button current' : 'dt-paging-button'
                            }
                            onClick={() => handlePageChange(i)}
                        >
                            {i}
                        </button>
                    )
                }
                buttons.push(<span className="ellipsis">...</span>)
                buttons.push(
                    <button
                        id={totalPages.toString()}
                        className="dt-paging-button"
                        onClick={() => handlePageChange(totalPages)}
                    >
                        {totalPages}
                    </button>
                )
            } else if (totalPages - currentPage < 4) {
                buttons.push(
                    <button
                        id={'1'}
                        className="dt-paging-button"
                        onClick={() => handlePageChange(1)}
                    >
                        1
                    </button>
                )
                buttons.push(<span className="ellipsis">...</span>)
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    buttons.push(
                        <button
                            id={i.toString()}
                            disabled={currentPage === i}
                            className={
                                currentPage === i ? 'dt-paging-button current' : 'dt-paging-button'
                            }
                            onClick={() => handlePageChange(i)}
                        >
                            {i}
                        </button>
                    )
                }
            } else {
                buttons.push(
                    <button
                        id={'1'}
                        className="dt-paging-button"
                        onClick={() => handlePageChange(1)}
                    >
                        1
                    </button>
                )
                buttons.push(<span className="ellipsis">...</span>)
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    buttons.push(
                        <button
                            id={i.toString()}
                            disabled={currentPage === i}
                            className={
                                currentPage === i ? 'dt-paging-button current' : 'dt-paging-button'
                            }
                            onClick={() => handlePageChange(i)}
                        >
                            {i}
                        </button>
                    )
                }
                buttons.push(<span className="ellipsis">...</span>)
                buttons.push(
                    <button
                        id={totalPages.toString()}
                        className="dt-paging-button"
                        onClick={() => handlePageChange(totalPages)}
                    >
                        {totalPages}
                    </button>
                )
            }
        } else {
            // Show all pages if less than or equal to 7
            for (let i = 1; i <= totalPages; i++) {
                buttons.push(
                    <button
                        id={i.toString()}
                        disabled={currentPage === i}
                        className={
                            currentPage === i ? 'dt-paging-button current' : 'dt-paging-button'
                        }
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                )
            }
        }

        // Add "Next" and "Last" buttons
        buttons.push(
            <button
                id="next"
                className={
                    currentPage === totalPages
                        ? 'dt-paging-button disabled next'
                        : 'dt-paging-button next'
                }
                onClick={(e) => {
                    if (currentPage === totalPages) {
                      e.preventDefault(); // FP-10424 Prevent the page change if we're on the last page
                      return;
                    }
                    handlePageChange(currentPage + 1);
                  }}
            >
                &gt;
            </button>
        )
        buttons.push(
            <button
                id="last"
                className={
                    currentPage === totalPages
                        ? 'dt-paging-button disabled last'
                        : 'dt-paging-button last'
                }
                onClick={() => handlePageChange(totalPages)}
            >
                &raquo;
            </button>
        )
        return buttons
    }

    const [originalData,setOriginalData] = useState<ResultType[]>([]);
    useEffect(()=>{
        if(originalData.length === 0 && MapperResults.length > 0){
            setOriginalData(MapperResults);
        }
    },[MapperResults]);
    const [disabledArrow,setDisabledArrow] = useState<{[key:string]:string | null}>({});
    const [sortColumn,setSortColumn ] = useState<any>(null);
    const [sortOrder,setSortOrder] = useState<'asc' | 'desc' | null>(null);
    // const [sortState,setSortState] = useState(null);

    const handleSort = (column:any) =>{
        setSortColumn((prevColumn:any) =>{
            const newSortOrder = prevColumn === column ? sortOrder === 'asc' 
            ? 'desc'
            : sortOrder === 'desc'
            ? null : 'asc'
            :'asc';
            setSortOrder(newSortOrder)
            setDisabledArrow((prevState:any) => ({
                ...prevState,
                [column]: newSortOrder === 'asc' ? 'asc' : newSortOrder === 'desc' ? 'desc' : null
            }));
            if(newSortOrder === null){
                setMapperResults([...originalData]);
            }
            else{
                const sortedData = [...MapperResults].sort((a,b)=>{
                    // Sort by the checkbox selection if column is 'firstColumn'
                    if (column === 'checkBoxColumn') {
                        const selectedForCurrentPage = selectedCheckboxes[currentPage] || [];
                        const aChecked = selectedForCurrentPage.includes(a.index) ?? false; // Check if the checkbox is selected
                        const bChecked = selectedForCurrentPage.includes(b.index) ?? false; // Check if the checkbox is selected

                        if (aChecked === bChecked) return 0; // If both are either checked or unchecked, keep their relative order

                        return newSortOrder === 'asc' ? (aChecked ? -1 : 1) : aChecked ? 1 : -1; // Sort checked first or last based on asc/desc
                    }
                    if(a[column] < b[column]) return newSortOrder === 'asc' ? -1 : 1;
                    if(a[column] > b[column]) return newSortOrder === 'asc' ? 1 : -1;
                    return 0;
                })
                setMapperResults(sortedData);
            }
            return column;
        })
    }

    const spanStyle:React.CSSProperties = {
        display:'block',
        textAlign:'right',
        fontSize:'10px',
        marginLeft: '5px'
    }

    return (
        <div id="datamapper_tool">
            <div>
                <div className="float-right margin-top-small">
                    <a id="link_backto_admin_tasks" href="index.html#AdminTasks">
                        &laquo; Back to Admin Tasks
                    </a>
                </div>
                <div className="header">{value}</div>
                <div className="float-right margin-top-small">
                <a id="link_backto_mapper_list" href="#" onClick={(e) => {
                    e.preventDefault();  //FP-10418 Prevent default link behavior 
                    GotoMapperList();  
                    }}>&laquo;  Back to Mapper List</a>
                </div>
                <div>{description}</div>
                {help && (
                    <Accordion
                        id="accordion_help"
                        headerClasses="select2"
                        contentClasses=""
                        data={[{ accordionHeader: accordionHeader, accordionContent: help }]}
                        onToggle={handleAccordionClick}
                    />
                )}
                <div className="padding-top-medium"></div>
                {ActionMode && (
                    <MapperActions
                        Action={MapperActionData.Action}
                        Rows={MapperActionData.Rows}
                        ParentFields={MapperActionData.ParentFields}
                        Key={Key}
                        FilterValues={FilterValues}
                        LoadMapperResults={LoadMapperResults}
                    />
                )}
                {!ActionMode && (
                    <span id="mapperResultTableWrapper" className="overflow-x-auto block">
                        <table id="mapperResultTable" className="admin-table datamapper-table mt-3 w-full">
                            <thead>
                                <DataMapperFilter
                                    columns={ColumnNames}
                                    Mapper={Mapper}
                                    ParentFields={[]}
                                    FilterValues={FilterValues}
                                    AddRow={AddRow}
                                    FiltersUpdated={FilterUpdated}
                                />
                                <tr id="dataHeader" className="admin-row-header">
                                    <th>
                                        {statusCode === 201 && (
                                            <span className="sort-icons" style={{ display: 'inline-block' }} onClick={() => handleSort('checkBoxColumn')}>
                                                <span style={spanStyle} className={`asc ${disabledArrow['checkBoxColumn'] === 'desc' ? 'disabled' : 'enabled'}`}>▲</span>
                                                <span style={spanStyle} className={`desc ${disabledArrow['checkBoxColumn'] === 'asc' ? 'disabled' : 'enabled'}`}>▼</span>
                                            </span>
                                        )}
                                    </th>
                                    {ColumnNames.map((columnName) => (
                                    <th 
                                    style={{
                                        width:'40px',
                                        minWidth:'40px',
                                        textAlign:'center',
                                        verticalAlign:'middle',
                                        position:'relative',
                                        cursor:'pointer'
                                    }}
                                        key={columnName} 
                                        className="text-center data-column"
                                        onClick={()=>handleSort(columnName)}
                                    >
                                        {columnName}
                                        {statusCode === 201 && (
                                            <>
                                            <span className="sort-icons" style={{ display: 'inline-block' }} onClick={()=>handleSort(columnName)}>
                                                <span style={spanStyle} className={`asc ${disabledArrow[columnName] === 'desc' ? 'disabled' : 'enabled'}`}>▲</span>
                                                <span style={spanStyle} className={`desc ${disabledArrow[columnName] === 'asc' ? 'disabled' : 'enabled'}`}>▼</span>
                                            </span>
                                            </>
                                        )}
                                    </th>
                                    ))}
                                    <th className='actions-column'>
                                        Actions
                                        {/* Sorting functionality for the Actions column */}
                                        {statusCode === 201 && (
                                            <span className="sort-icons" style={{ display: 'inline-block' }} onClick={() => handleSort('actions')}>
                                                <span style={spanStyle} className={`asc ${disabledArrow['actions'] === 'asc' ? 'disabled' : 'enabled'}`}>▲</span>
                                                <span style={spanStyle} className={`desc ${disabledArrow['actions'] === 'desc' ? 'disabled' : 'enabled'}`}>▼</span>
                                            </span>
                                        )}
                                    </th>
                                    {/* <th> Actions </th> */}
                                </tr>
                            </thead>
                            <tbody id="dataDataMapperResults">
                                {statusCode === -1 && (
                                    <tr id="no_mapper_row" className="admin-row-data text-center">
                                        <td colSpan={ColumnNames.length + 2}>
                                            {' '}
                                            Please Narrow Results by Filter{' '}
                                        </td>
                                    </tr>
                                )}
                                {statusCode === 0 && (
                                    <tr id="no_mapper_row" className="admin-row-data text-center">
                                        <td colSpan={ColumnNames.length + 2}></td>
                                    </tr>
                                )}
                                {statusCode === 201 &&
                                    MapperResults.length > 0 &&
                                    paginatedData.map((result, index) => (
                                        <tr>
                                            <td className="sorting_1">
                                                <input
                                                    type="checkbox"
                                                    name={index.toString()}
                                                    id={`mapper_select_${index}`}
                                                    checked = {(selectedCheckboxes[currentPage] || []).includes(index)}
                                                    onChange={() => handleCheckboxChange(index)}
                                                    className="mt-3 mapper_checkbox checkbox-height"
                                                />
                                            </td>
                                            {ColumnNames.map((columnName) => (
                                                <td>
                                                <div className="data_results_field result_data overflow-hidden text-ellipsis whitespace-nowrap">
                                                    {String(result[columnName])}
                                                </div>
                                            </td>
                                            ))}
                                            <td style={{ minWidth: '170px'}}>
                                                {showActionEdit && (
                                                        <button
                                                            title="Edit the data for this entry"
                                                            data-rowindex={index}
                                                            className="app-button data_result_action"
                                                            onClick={() => EditRow(index)}
                                                        >
                                                            Edit
                                                        </button>
                                                )}
                                                {showActionDelete && (
                                                        <button
                                                            title="Delete the data for this entry"
                                                            data-rowindex={index}
                                                            className="app-button data_result_action"
                                                            onClick={() => DeleteRow(index)}
                                                        >
                                                            Delete
                                                        </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                {statusCode === 201 && MapperResults.length === 0 && (
                                    <tr id="no_mapper_row" className="admin-row-data text-center">
                                        <td colSpan={ColumnNames.length + 2}>
                                            {' '}
                                            No matching results found{' '}
                                        </td>
                                    </tr>
                                )}
                                {statusCode === 500 && (
                                    <tr id="no_mapper_row" className="admin-row-data text-center">
                                        <td colSpan={ColumnNames.length + 2}>
                                            {' '}
                                            Failed to fetch data{' '}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </span>
                )}
                {!ActionMode && statusCode === 201 && MapperResults.length > 0 && (
                    <div className="pagination-controls">
                        <select value={recordsPerPage} onChange={handlePageSizeChange}>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={250}>250</option>
                            <option value={MapperResults.length}>All</option>
                        </select>
                        <span>entries per page</span>
                        <div className="page-numbers">
                            {generatePaginationButtons()}
                        </div>
                    </div>)}
            </div>
        </div>
    )
}
export default IndividualMapper
